import { create } from 'zustand';
import { INICIO_PADRAO, normalizarInicio } from '../utils/numeracaoAnexos.js';

const ensureObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value;
};

const ensureArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value;
};

const normalizeAssignmentValue = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const id = String(value).trim();
    return id ? { id, name: '', url: '' } : null;
  }
  if (typeof value === 'object') {
    const id = String(value?.id || '').trim();
    if (!id) return null;
    const name = String(value?.name || '').trim();
    const url = String(value?.url || '').trim();
    const out = { id, name, url };
    if (Array.isArray(value.multiFiles)) out.multiFiles = value.multiFiles;
    return out;
  }
  return null;
};

const normalizeAssignments = (value) => {
  const raw = ensureObject(value);
  const out = {};
  for (const [slotId, v] of Object.entries(raw)) {
    const norm = normalizeAssignmentValue(v);
    if (norm) out[String(slotId)] = norm;
  }
  return out;
};

const normalizeSlotNames = (value) => {
  const raw = ensureObject(value);
  const out = {};
  for (const [slotId, v] of Object.entries(raw)) {
    out[String(slotId)] = String(v ?? '');
  }
  return out;
};

const normalizeClientRequested = (value) => {
  const raw = ensureObject(value);
  const out = {};
  for (const [slotId, v] of Object.entries(raw)) {
    if (v === true || v === false) out[String(slotId)] = v;
  }
  return out;
};

// Numeração dos anexos (24/09/2026): "começar em N" da peça (padrão 1). Vive AO LADO dos
// 4 campos do estado da peça e por isso precisa entrar em TODOS os lugares que os
// repetem por nome (setHydratedState, reset, hydrateWorkspace, getStateSnapshot,
// revertState). Se algum ficar de fora, o campo é descartado em silêncio — e o
// reset() que não o zera deixaria o número da peça anterior VAZAR para a próxima.
const inicioSeguro = (valor, slotOrder) => normalizarInicio(valor, ensureArray(slotOrder).length);

export default create((set, get) => ({
  assignments: {},
  slotNames: {},
  slotOrder: [],
  clientRequested: {},
  numeroInicialAnexos: INICIO_PADRAO,

  isHydrated: false,

  // FIX FLICKER CADERNO DE AUTOS (22/08/2026): antes, o controle de "esse
  // caderno já foi hidratado" vivia num useRef DENTRO de useFileHub.js — e
  // um useRef morre junto com o componente toda vez que FileHubArea
  // desmonta (troca de aba, ida/volta em "Caderno de Autos"). Resultado:
  // a cada remonte, o código achava que nunca tinha hidratado aquele
  // caderno (mesmo com o dado certo já aqui na store) e disparava de novo
  // toda a lógica pesada de recuperação (HYDRATE-RECOVERY/DEEP-RECOVERY em
  // useFileHub.js), competindo com o dado antigo ainda visível na tela.
  // Campo vive AQUI (na store, que sobrevive ao remount) — não como
  // useRef de componente — pra resolver a causa raiz, não só escondê-la.
  // Deliberadamente SEM acoplamento a reset()/resetWorkspace(): esses são
  // usados também pra reset de reconexão de pasta do Drive
  // (connectToDriveFolder), que não é troca de caderno — useFileHub.js
  // decide explicitamente quando setHydratedWorkspaceKey muda.
  hydratedWorkspaceKey: null,

  setAssignments: (updater) => {
    set((state) => {
      const prev = state.assignments;
      const nextRaw = typeof updater === 'function' ? updater(prev) : updater;
      if (nextRaw === prev) return state;
      const next = normalizeAssignments(nextRaw);
      return { assignments: next };
    });
  },

  setSlotNames: (updater) => {
    set((state) => {
      const prev = state.slotNames;
      const nextRaw = typeof updater === 'function' ? updater(prev) : updater;
      if (nextRaw === prev) return state;
      const next = normalizeSlotNames(nextRaw);
      return { slotNames: next };
    });
  },

  setSlotOrder: (updater) => {
    set((state) => {
      const prev = state.slotOrder;
      const nextRaw = typeof updater === 'function' ? updater(prev) : updater;
      if (nextRaw === prev) return state;
      const next = ensureArray(nextRaw).map(String);
      return { slotOrder: next };
    });
  },

  setClientRequested: (updater) => {
    set((state) => {
      const prev = state.clientRequested;
      const nextRaw = typeof updater === 'function' ? updater(prev) : updater;
      if (nextRaw === prev) return state;
      return { clientRequested: normalizeClientRequested(nextRaw) };
    });
  },

  setNumeroInicialAnexos: (updater) => {
    set((state) => {
      const prev = state.numeroInicialAnexos;
      const nextRaw = typeof updater === 'function' ? updater(prev) : updater;
      const next = inicioSeguro(nextRaw, state.slotOrder);
      if (next === prev) return state;
      return { numeroInicialAnexos: next };
    });
  },

  setHydratedState: (data) => {
    const d = ensureObject(data);
    const hydrated = {
      assignments: normalizeAssignments(d.assignments),
      slotNames: normalizeSlotNames(d.slotNames),
      slotOrder: ensureArray(d.slotOrder).map(String),
      clientRequested: normalizeClientRequested(d.clientRequested),
      numeroInicialAnexos: inicioSeguro(d.numeroInicialAnexos, d.slotOrder),
      isHydrated: true,
    };
    console.log('[STORE] setHydratedState — slotOrder:', hydrated.slotOrder, 'assignments keys:', Object.keys(hydrated.assignments));
    set(hydrated);
  },

  reset: () => {
    console.log('[STORE] reset() called — stack:', new Error().stack?.split('\n').slice(1, 4).join(' < '));
    set({ assignments: {}, slotNames: {}, slotOrder: [], clientRequested: {}, numeroInicialAnexos: INICIO_PADRAO, isHydrated: false });
  },

  resetWorkspace: () => {
    get().reset();
  },

  hydrateWorkspace: (data) => {
    const d = ensureObject(data);
    if (!d || Object.keys(d).length === 0) return;
    set({
      assignments: normalizeAssignments(d.assignments),
      slotNames: normalizeSlotNames(d.slotNames),
      slotOrder: ensureArray(d.slotOrder).map(String),
      clientRequested: normalizeClientRequested(d.clientRequested),
      numeroInicialAnexos: inicioSeguro(d.numeroInicialAnexos, d.slotOrder),
      isHydrated: true,
    });
  },

  getStateSnapshot: () => {
    const s = get();
    return {
      assignments: normalizeAssignments(s.assignments),
      slotNames: normalizeSlotNames(s.slotNames),
      slotOrder: ensureArray(s.slotOrder).map(String),
      clientRequested: normalizeClientRequested(s.clientRequested),
      numeroInicialAnexos: inicioSeguro(s.numeroInicialAnexos, s.slotOrder),
    };
  },

  // CORE FIX: Rollback otimista — desfaz ilusão de persistência quando Firestore rejeita (403)
  revertState: (snapshot) => {
    const d = ensureObject(snapshot);
    console.log('[STORE] revertState() — rollback para estado confirmado pelo Firestore');
    set({
      assignments: normalizeAssignments(d.assignments),
      slotNames: normalizeSlotNames(d.slotNames),
      slotOrder: ensureArray(d.slotOrder).map(String),
      clientRequested: normalizeClientRequested(d.clientRequested),
      numeroInicialAnexos: inicioSeguro(d.numeroInicialAnexos, d.slotOrder),
    });
  },

  setHydratedWorkspaceKey: (key) => {
    set({ hydratedWorkspaceKey: key || null });
  },

  // Marca workspace virgem como hidratado (desbloqueia syncWorkspaceNow)
  markHydrated: () => {
    if (!get().isHydrated) {
      console.log('[STORE] markHydrated() — workspace virgem, desbloqueando sync');
      set({ isHydrated: true });
    }
  },
}));
